import { Component, Input, WritableSignal, signal, effect } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { OverlayModule } from '@angular/cdk/overlay';
import { FormErrorPipe } from 'src/ui/pipes/form-error.pipe';
import { DropdownService } from 'src/utils/dropdown.service';

interface CalendarDay {
  date: Date;
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
}

@Component({
  selector: 'input-date',
  imports: [ReactiveFormsModule, CommonModule, FormErrorPipe, FormsModule, OverlayModule],
  standalone: true,
  templateUrl: './input-date.component.html',
  styleUrl: './input-date.component.scss'
})
export class InputDateComponent {
  @Input() control: AbstractControl = new FormControl();
  @Input() label: string = '';
  @Input() assistiveText: string = '';
  @Input() placeholder: string = 'DD/MM/AAAA';
  @Input() isReadOnly: boolean = false;
  @Input() showError: boolean = true;

  isOpen: WritableSignal<boolean> = signal(false);
  showMonthDropdown = false;
  showYearDropdown = false;
  currentMonth: number;
  currentYear: number;
  calendarDays: CalendarDay[] = [];
  selectedDate: Date | null = null;

  private readonly dropdownId: string = `date-${Math.random().toString(36).slice(2, 9)}`;

  months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  
  daysOfWeek = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'];
  
  years: number[] = [];

  constructor(private readonly dropdownService: DropdownService) {
    const today = new Date();
    this.currentMonth = today.getMonth();
    this.currentYear = today.getFullYear();
    
    // Generar años (100 años atrás y 10 adelante)
    const startYear = this.currentYear - 100;
    const endYear = this.currentYear + 10;
    for (let year = startYear; year <= endYear; year++) {
      this.years.push(year);
    }
    
    this.updateCalendar();

    // Mantener el estado de apertura sincronizado de forma global
    effect(() => {
      const openId = this.dropdownService.getOpenDropdown()();
      const shouldBeOpen = openId === this.dropdownId;
      this.isOpen.set(shouldBeOpen);

      if (!shouldBeOpen) {
        this.showMonthDropdown = false;
        this.showYearDropdown = false;
      }
    });
  }

  ngOnInit() {
    // Inicializar selectedDate si el control ya tiene un valor
    const controlValue = this.formControl.value;
    if (controlValue) {
      this.selectedDate = new Date(controlValue);
      this.currentMonth = this.selectedDate.getMonth();
      this.currentYear = this.selectedDate.getFullYear();
      this.updateCalendar();
    }
  }

  get formControl(): FormControl {
    return this.control as FormControl;
  }

  get isInvalid(): boolean {
    return this.control.invalid && (this.control.dirty || this.control.touched);
  }

  get isDisabled(): boolean {
    return this.formControl.disabled;
  }

  get displayValue(): string {
    if (this.selectedDate) {
      const day = this.selectedDate.getDate().toString().padStart(2, '0');
      const month = (this.selectedDate.getMonth() + 1).toString().padStart(2, '0');
      const year = this.selectedDate.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return '';
  }

  toggleCalendar(): void {
    if (!this.isReadOnly && !this.isDisabled) {
      this.dropdownService.toggleDropdown(this.dropdownId);
      if (this.isOpen()) {
        if (this.selectedDate) {
          this.currentMonth = this.selectedDate.getMonth();
          this.currentYear = this.selectedDate.getFullYear();
        }
        this.updateCalendar();
      }
    }
  }

  closeCalendar(): void {
    this.dropdownService.closeDropdown();
    this.showMonthDropdown = false;
    this.showYearDropdown = false;
    this.formControl.markAsTouched();
  }

  updateCalendar(): void {
    this.calendarDays = [];
    const firstDay = new Date(this.currentYear, this.currentMonth, 1);
    const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Días del mes anterior
    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(this.currentYear, this.currentMonth, -i);
      date.setHours(0, 0, 0, 0);
      this.calendarDays.push({
        date,
        day: date.getDate(),
        isCurrentMonth: false,
        isToday: false,
        isSelected: false
      });
    }

    // Días del mes actual
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(this.currentYear, this.currentMonth, day);
      date.setHours(0, 0, 0, 0);
      const isToday = date.getTime() === today.getTime();
      const isSelected = this.selectedDate ? date.getTime() === this.selectedDate.getTime() : false;
      
      this.calendarDays.push({
        date,
        day,
        isCurrentMonth: true,
        isToday,
        isSelected
      });
    }

    // Días del mes siguiente - solo agregar hasta completar la última semana
    const currentLength = this.calendarDays.length;
    const remainingInLastWeek = currentLength % 7 === 0 ? 0 : 7 - (currentLength % 7);
    
    for (let day = 1; day <= remainingInLastWeek; day++) {
      const date = new Date(this.currentYear, this.currentMonth + 1, day);
      date.setHours(0, 0, 0, 0);
      this.calendarDays.push({
        date,
        day,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false
      });
    }
  }

  selectDate(day: CalendarDay): void {
    if (day.isCurrentMonth) {
      this.selectedDate = day.date;
      this.formControl.setValue(this.selectedDate);
      this.formControl.markAsTouched();
      this.updateCalendar();
      this.closeCalendar();
    }
  }

  previousMonth(): void {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.updateCalendar();
  }

  nextMonth(): void {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.updateCalendar();
  }

  clearDate(): void {
    this.selectedDate = null;
    this.formControl.setValue(null);
    this.formControl.markAsTouched();
    this.updateCalendar();
    this.closeCalendar();
  }

  selectToday(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.selectedDate = today;
    this.currentMonth = today.getMonth();
    this.currentYear = today.getFullYear();
    this.formControl.setValue(this.selectedDate);
    this.formControl.markAsTouched();
    this.updateCalendar();
    this.closeCalendar();
  }

  toggleMonthDropdown(): void {
    this.showMonthDropdown = !this.showMonthDropdown;
    this.showYearDropdown = false;
    
    if (this.showMonthDropdown) {
      setTimeout(() => {
        const element = document.getElementById(`month-${this.currentMonth}`);
        if (element) {
          element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 0);
    }
  }

  toggleYearDropdown(): void {
    this.showYearDropdown = !this.showYearDropdown;
    this.showMonthDropdown = false;
    
    if (this.showYearDropdown) {
      setTimeout(() => {
        const element = document.getElementById(`year-${this.currentYear}`);
        if (element) {
          element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 0);
    }
  }

  selectMonth(month: number): void {
    this.currentMonth = month;
    this.showMonthDropdown = false;
    this.updateCalendar();
  }

  selectYear(year: number): void {
    this.currentYear = year;
    this.showYearDropdown = false;
    this.updateCalendar();
  }

  getWeeks(): CalendarDay[][] {
    const weeks: CalendarDay[][] = [];
    for (let i = 0; i < this.calendarDays.length; i += 7) {
      weeks.push(this.calendarDays.slice(i, i + 7));
    }
    return weeks;
  }

  onOverlayOutsideClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.date-picker-container')) {
      return;
    }
    if (this.isOpen()) {
      this.formControl.markAsTouched();
      this.dropdownService.closeDropdown();
    }
    this.showMonthDropdown = false;
    this.showYearDropdown = false;
  }
}
